import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import Breadcrumb from '../components/Breadcrumb.jsx';

const RANK = { member: 1, admin: 2, owner: 3 };

export default function TeamPage() {
  const { teamId } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      setTeam(await api(`/teams/${teamId}`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  if (auth.loading) return <div className="app"><div className="loading">Loading…</div></div>;
  if (!auth.user) return <Navigate to="/login" replace />;

  const act = (fn) => async () => {
    setMsg(null);
    try {
      await fn();
      await load();
      await auth.refresh();
    } catch (err) {
      setMsg(err.message);
    }
  };

  const isAdmin = team && RANK[team.role] >= RANK.admin;
  const isOwner = team?.role === 'owner';

  return (
    <div className="app narrow">
      <Breadcrumb items={[{ label: 'My sites', to: `/sites?team=${teamId}` }, { label: team?.name ?? 'Team' }]} />
      {error && <div className="banner err">{error}</div>}
      {team && (
        <>
          <header className="masthead">
            <div>
              <h1>{team.name}</h1>
              <p className="sub">You are {team.role === 'admin' ? 'an' : 'a'} {team.role} of this team.</p>
            </div>
          </header>

          {isAdmin && (
            <section className="form-card">
              <h3>Invite people</h3>
              <p className="muted">
                Share this code. Anyone with it can join as a member, so rotate it if it leaks.
              </p>
              <div className="invite-code">{team.inviteCode}</div>
              <div className="form-actions">
                <button className="btn" onClick={() => navigator.clipboard?.writeText(team.inviteCode)}>
                  Copy
                </button>
                <button
                  className="btn"
                  onClick={act(async () => {
                    if (window.confirm('Make a new code? The current one will stop working.')) {
                      await api(`/teams/${teamId}/invite-code`, { method: 'POST' });
                    }
                  })}
                >
                  Rotate code
                </button>
              </div>
            </section>
          )}

          <h3>Members ({team.members.length})</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="left">Member</th>
                  <th className="left">Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {team.members.map((m) => {
                  const self = m.id === auth.user.id;
                  const canRemove = !self && isAdmin && (RANK[m.role] < RANK.admin || isOwner);
                  return (
                    <tr key={m.id}>
                      <td className="left">
                        {m.displayName || m.email}
                        <span className="place">{m.email}</span>
                      </td>
                      <td className="left">
                        {isOwner ? (
                          <select
                            value={m.role}
                            onChange={(e) =>
                              act(() =>
                                api(`/teams/${teamId}/members/${m.id}`, { method: 'PATCH', body: { role: e.target.value } }),
                              )()
                            }
                          >
                            <option value="owner">owner</option>
                            <option value="admin">admin</option>
                            <option value="member">member</option>
                          </select>
                        ) : (
                          m.role
                        )}
                      </td>
                      <td>
                        {canRemove && (
                          <button
                            className="btn-link"
                            onClick={act(async () => {
                              if (window.confirm(`Remove ${m.displayName || m.email} from ${team.name}?`)) {
                                await api(`/teams/${teamId}/members/${m.id}`, { method: 'DELETE' });
                              }
                            })}
                          >
                            Remove
                          </button>
                        )}
                        {self && (
                          <button
                            className="btn-link"
                            onClick={async () => {
                              if (!window.confirm(`Leave ${team.name}?`)) return;
                              try {
                                await api(`/teams/${teamId}/members/${m.id}`, { method: 'DELETE' });
                                await auth.refresh();
                                navigate('/sites');
                              } catch (err) {
                                setMsg(err.message);
                              }
                            }}
                          >
                            Leave team
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {msg && <div className="banner err">{msg}</div>}

          {isOwner && (
            <section className="danger-zone">
              <h3>Delete team</h3>
              <p className="muted">Deletes the team, all its sites and their incident history. This can&rsquo;t be undone.</p>
              <button
                className="btn danger"
                onClick={async () => {
                  const typed = window.prompt(`Type the team name (${team.name}) to delete it permanently.`);
                  if (typed !== team.name) return;
                  try {
                    await api(`/teams/${teamId}`, { method: 'DELETE' });
                    await auth.refresh();
                    navigate('/sites');
                  } catch (err) {
                    setMsg(err.message);
                  }
                }}
              >
                Delete team
              </button>
            </section>
          )}
          <p className="muted">
            <Link to={`/sites?team=${teamId}`}>Back to this team&rsquo;s sites</Link>
          </p>
        </>
      )}
    </div>
  );
}
